import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, ClipboardCheck, Eye, X } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { RecordDetailModal } from '../database/RecordDetailModal'
import { ProjectReviewQueue } from './ProjectReviewQueue'
import { Swatch } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { Field, Textarea } from '@/components/ui/Field'
import { Card, EmptyState, LoadingPanel, Notice } from '@/components/ui/misc'
import { api, ApiError } from '@/lib/api'
import { formatRelative } from '@/lib/utils'

interface QueueRecord {
  id: string
  code: string
  kind: 'experiment' | 'reference'
  title: string
  swatchHex: string | null
  researcherName: string | null
  submittedAt: string | null
  completionPct: number
  dyeSourceName: string | null
  approvals: number
}

export function ReviewQueue() {
  const queryClient = useQueryClient()
  const [rejecting, setRejecting] = useState<QueueRecord | null>(null)
  const [reason, setReason] = useState('')
  const [previewing, setPreviewing] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['review', 'queue'],
    queryFn: () => api.get<{ records: QueueRecord[]; threshold: number }>('/review/queue'),
  })

  const decide = useMutation({
    mutationFn: ({
      id,
      decision,
      note,
    }: {
      id: string
      decision: 'approve' | 'reject'
      note?: string
    }) => api.post<{ status: string; approvals: number }>(`/review/${id}/decision`, {
      decision,
      reason: note,
    }),
    onSuccess: async (result) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['review'] }),
        queryClient.invalidateQueries({ queryKey: ['workspace'] }),
        queryClient.invalidateQueries({ queryKey: ['browse'] }),
      ])
      setRejecting(null)
      setReason('')
      toast.success(
        result.status === 'published'
          ? 'Approved. The record is now published.'
          : result.status === 'rejected'
            ? 'Returned to the author with your feedback.'
            : `Approved. ${result.approvals} of ${data?.threshold ?? 1} approvals so far.`,
      )
    },
    onError: (failure) =>
      toast.error(failure instanceof ApiError ? failure.message : 'That review did not save.'),
  })

  const projects = useQuery({
    queryKey: ['review', 'projects'],
    queryFn: () => api.get<{ projects: unknown[] }>('/review/projects'),
  })

  const records = data?.records ?? []
  const hasProjects = (projects.data?.projects.length ?? 0) > 0

  return (
    <>
      <header className="mb-4">
        <h2 className="font-display text-2xl font-semibold text-ink">Review queue</h2>
        <p className="mt-0.5 text-[13px] text-ink-muted">
          Submissions from other researchers waiting for approval.
        </p>
      </header>

      <div className="mb-4">
        <Notice tone="neutral" title="How publishing works" icon={<ClipboardCheck className="size-4" />}>
          One approval from a super user or admin publishes a record. Projects are approved as a
          whole: every experiment in them is published together. A request for changes returns the
          submission to its author, who can revise and resubmit.
        </Notice>
      </div>

      <ProjectReviewQueue onPreview={setPreviewing} />

      {hasProjects && records.length > 0 && (
        <h3 className="pb-2 font-display text-lg font-semibold text-ink">Individual submissions</h3>
      )}

      {isLoading ? (
        <LoadingPanel label="Loading the queue" />
      ) : records.length === 0 ? (
        hasProjects ? null : (
        <EmptyState
          icon={<ClipboardCheck className="size-7" />}
          title="Nothing to review"
          description="You are up to date. New submissions from other researchers will appear here."
        />
        )
      ) : (
        <div className="space-y-2">
          {records.map((record) => (
            <Card key={record.id} className="p-4">
              <div className="flex items-start gap-3">
                <Swatch hex={record.swatchHex} size="lg" className="rounded-[var(--radius-control)]" />

                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-faint">
                    {record.code} · {record.kind}
                  </p>
                  <h3 className="truncate font-display text-[15px] font-semibold text-ink">
                    {record.title || 'Untitled record'}
                  </h3>
                  <p className="mt-0.5 text-[12px] text-ink-muted">
                    {record.researcherName} · {record.dyeSourceName ?? 'no dye source'} · submitted{' '}
                    {formatRelative(record.submittedAt)}
                  </p>
                </div>

                <div className="flex shrink-0 items-center gap-2">
                  <Button variant="secondary" size="sm" onClick={() => setPreviewing(record.id)}>
                    <Eye className="size-3.5" />
                    Read
                  </Button>
                  <Button
                    size="sm"
                    loading={decide.isPending}
                    onClick={() => decide.mutate({ id: record.id, decision: 'approve' })}
                  >
                    <Check className="size-3.5" />
                    Approve
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setRejecting(record)
                      setReason('')
                    }}
                  >
                    <X className="size-3.5" />
                    Request changes
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog
        open={Boolean(rejecting)}
        onOpenChange={(open) => !open && setRejecting(null)}
        size="sm"
        title="Request changes"
        description={rejecting ? `${rejecting.code} · ${rejecting.title}` : undefined}
        footer={
          <>
            <Button variant="secondary" onClick={() => setRejecting(null)}>
              Cancel
            </Button>
            <Button
              loading={decide.isPending}
              disabled={!reason.trim()}
              onClick={() =>
                rejecting &&
                decide.mutate({ id: rejecting.id, decision: 'reject', note: reason.trim() })
              }
            >
              Send back to author
            </Button>
          </>
        }
      >
        <Field
          label="What needs changing?"
          htmlFor="reason"
          required
          hint="The author sees this verbatim, so be specific about which fields or methods need work."
        >
          <Textarea
            id="reason"
            rows={4}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="The mordant concentration is missing. Please record it as % owf."
          />
        </Field>
      </Dialog>

      <RecordDetailModal
        recordId={previewing}
        onOpenChange={(open) => !open && setPreviewing(null)}
      />
    </>
  )
}
